import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { logoutUser } from './authService';
import { myFirebaseAuth } from '../../firebase/config';
import {
  AppFile,
  CreditApplicationStep,
  CreditApplicationTemplateType,
  CreditApplicationTemplateUpdate,
  InternalSignatureDocumentDefinition,
} from '@dill/dill-shared';

export const createSupplierCreditApplicationTemplate = createAsyncThunk(
  'creditApplicationTemplates/createCreditApplicationTemplate',
  async (
    {
      supplierId,
      steps,
      internalSignatureDocuments,
      applicationType,
      name,
      subsidiaryName,
      bankReferenceRequiredFields,
      tradeReferenceFile,
      approvalMessage,
      approvalSubjectLine,
      rejectCODMessage,
      rejectSubjectLine,
      bankReferenceFile,
      welcomeAttachments,
      customizedWelcomeTemplate,
    }: {
      supplierId: string;
      steps: CreditApplicationStep[];
      internalSignatureDocuments?: InternalSignatureDocumentDefinition[];
      applicationType: CreditApplicationTemplateType;
      name?: string;
      subsidiaryName?: string | null;
      bankReferenceRequiredFields?: string[];
      tradeReferenceFile?: AppFile | null;
      approvalMessage?: string | null;
      approvalSubjectLine?: string | null;
      rejectCODMessage?: string | null;
      rejectSubjectLine?: string | null;
      bankReferenceFile?: AppFile | null;
      welcomeAttachments?: AppFile[];
      customizedWelcomeTemplate?: boolean;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      let additionalDetails = {};
      if (internalSignatureDocuments && internalSignatureDocuments.length > 0) {
        additionalDetails = { internalSignatureDocuments };
      }
      if (bankReferenceRequiredFields && bankReferenceRequiredFields.length > 0) {
        additionalDetails = { ...additionalDetails, bankReferenceRequiredFields };
      }
      if (tradeReferenceFile) {
        additionalDetails = { ...additionalDetails, tradeReferenceFile };
      }
      if (approvalMessage) {
        additionalDetails = { ...additionalDetails, approvalMessage };
      }
      if (approvalSubjectLine) {
        additionalDetails = { ...additionalDetails, approvalSubjectLine };
      }
      if (rejectCODMessage) {
        additionalDetails = { ...additionalDetails, rejectCODMessage };
      }
      if (rejectSubjectLine) {
        additionalDetails = { ...additionalDetails, rejectSubjectLine };
      }
      if (bankReferenceFile) {
        additionalDetails = { ...additionalDetails, bankReferenceFile };
      }
      if (welcomeAttachments && welcomeAttachments.length > 0) {
        additionalDetails = { ...additionalDetails, welcomeAttachments };
      }
      if (customizedWelcomeTemplate !== undefined) {
        additionalDetails = { ...additionalDetails, customizedWelcomeTemplate };
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplicationTemplates/createCreditApplicationTemplate`,
        {
          supplierId,
          steps,
          applicationType,
          name,
          ...(subsidiaryName?.trim() ? { subsidiaryName: subsidiaryName.trim() } : {}),
          ...additionalDetails,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getCreditApplicationTemplates = createAsyncThunk(
  'creditApplicationTemplates/getCreditApplicationTemplates',
  async (
    {
      supplierId,
      applicationType,
    }: {
      supplierId: string;
      // Omitted: credit/COD application templates only. See getCreditApplicationTemplatesController.
      applicationType?: CreditApplicationTemplateType | 'ALL';
    },
    thunkApi
  ) => {
    try {
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/creditApplications/getCreditApplicationTemplates`,
        {
          headers: {},
          params: { supplierId, ...(applicationType ? { applicationType } : {}) },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getCreditApplicationTemplate = createAsyncThunk(
  'creditApplicationTemplates/getCreditApplicationTemplate',
  async ({ templateId }: { templateId: string }, thunkApi) => {
    try {
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/creditApplications/getCreditApplicationTemplate/${templateId}`,
        {
          headers: {},
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateCreditApplicationTemplateByAdmin = createAsyncThunk(
  'creditApplicationTemplates/updateCreditApplicationTemplateByAdmin',
  async (
    {
      supplierId,
      templateId,
      steps,
    }: {
      supplierId: string;
      templateId: string;
      steps: CreditApplicationStep[];
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplicationTemplates/updateCreditApplicationTemplatesByAdmin/${templateId}`,
        { steps },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateCreditApplicationTemplate = createAsyncThunk(
  'creditApplicationTemplates/updateCreditApplicationTemplate',
  async (
    {
      supplierId,
      templateId,
      templateDetails,
    }: {
      supplierId: string;
      templateId: string;
      templateDetails: CreditApplicationTemplateUpdate;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplicationTemplates/updateCreditApplicationTemplate/${templateId}`,
        { ...templateDetails },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
